import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid2 as Grid, Card, CardContent, Chip,
  CircularProgress, Alert, Stack, Avatar, Button,
  LinearProgress, Divider,
} from '@mui/material';
import {
  TrendingUp, Receipt, AccountBalanceWallet, ArrowForward,
  Payment, CheckCircle, Schedule, Warning,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { nowCv } from '../../utils/date';

interface DashboardStats {
  hoje: { transacoes: number; volume: number; comissoes: number };
  mes: { transacoes: number; volume: number; comissoes: number };
  total: { transacoes: number; comissoesAReceber: number };
  agente: { nome: string; codigo: string; estado: string; contrato: any };
  wallet: { saldo: number; saldoMinimo: number; estado: string } | null;
}

interface Transacao {
  ID: string;
  REFERENCIA: string;
  MONTANTE: number;
  COMISSAO_VALOR: number;
  ESTADO: string;
  DT_TRANSACAO: string;
  ENTIDADE_CODIGO: string;
}

const ESTADO_COLOR: Record<string, 'default' | 'warning' | 'success' | 'error'> = {
  PENDENTE: 'warning',
  PROCESSADO: 'success',
  FALHOU: 'error',
  ESTORNADO: 'default',
};

const ESTADO_LABEL: Record<string, string> = {
  PENDENTE: 'Pendente',
  PROCESSADO: 'Processado',
  FALHOU: 'Falhou',
  ESTORNADO: 'Estornado',
};

const fmt = (n: number) =>
  new Intl.NumberFormat('pt-CV', { style: 'currency', currency: 'CVE', maximumFractionDigits: 0 }).format(n);

const fmtDate = (dt: string) =>
  new Date(dt).toLocaleString('pt-PT', { timeZone: 'Atlantic/Cape_Verde',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const getGreeting = () => {
  const h = nowCv().getUTCHours(); // hora de CV
  if (h < 12) return 'Bom dia';
  if (h < 18) return 'Boa tarde';
  return 'Boa noite';
};

const formatFullDate = () =>
  new Date().toLocaleDateString('pt-PT', { timeZone: 'Atlantic/Cape_Verde',
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

export default function AgenteDashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [transacoes, setTransacoes] = useState<Transacao[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.allSettled([
      api.get('/agente/dashboard'),
      api.get('/agente/transacoes', { params: { limit: 5 } }),
    ]).then(([statsRes, txRes]) => {
      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data?.data);
      if (txRes.status === 'fulfilled') setTransacoes(txRes.value.data?.data || []);
      if (statsRes.status === 'rejected') setError('Erro ao carregar dashboard');
    }).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={400} gap={2}>
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary">A carregar dashboard…</Typography>
      </Box>
    );
  }

  const wallet = stats?.wallet;
  const isLowBalance = wallet ? wallet.saldo < wallet.saldoMinimo : false;
  const walletPercent = wallet && wallet.saldoMinimo > 0
    ? Math.min(100, (wallet.saldo / (wallet.saldoMinimo * 3)) * 100)
    : 100;

  const statCards = [
    {
      label: 'Volume Hoje',
      value: fmt(stats?.hoje?.volume || 0),
      sub: `${stats?.hoje?.transacoes || 0} transacção${(stats?.hoje?.transacoes || 0) !== 1 ? 'ões' : ''}`,
      icon: <TrendingUp sx={{ fontSize: 22 }} />,
      color: 'primary',
      bgColor: 'primary.50',
      iconBg: 'primary.100',
    },
    {
      label: 'Comissões Hoje',
      value: fmt(stats?.hoje?.comissoes || 0),
      sub: 'Ganhos do dia',
      icon: <Receipt sx={{ fontSize: 22 }} />,
      color: 'success',
      bgColor: 'success.50',
      iconBg: 'success.100',
    },
    {
      label: 'Volume do Mês',
      value: fmt(stats?.mes?.volume || 0),
      sub: `${stats?.mes?.transacoes || 0} transacção${(stats?.mes?.transacoes || 0) !== 1 ? 'ões' : ''}`,
      icon: <Schedule sx={{ fontSize: 22 }} />,
      color: 'info',
      bgColor: 'info.50',
      iconBg: 'info.100',
    },
    {
      label: 'Comissões a Receber',
      value: fmt(stats?.total?.comissoesAReceber || 0),
      sub: 'Não facturadas',
      icon: <CheckCircle sx={{ fontSize: 22 }} />,
      color: 'warning',
      bgColor: 'warning.50',
      iconBg: 'warning.100',
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1100, mx: 'auto' }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Hero / Greeting */}
      <Box
        sx={{
          mb: 3,
          p: 3,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', mb: 0.25, textTransform: 'capitalize' }}>
            {formatFullDate()}
          </Typography>
          <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.2, mb: 0.5 }}>
            {getGreeting()}, {stats?.agente?.nome?.split(' ')[0] || 'Agente'}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)' }}>
              Código: {stats?.agente?.codigo}
            </Typography>
            <Chip
              label={stats?.agente?.estado || 'ATIVO'}
              size="small"
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontWeight: 600,
                fontSize: 10,
                height: 20,
              }}
            />
          </Stack>
        </Box>
        <Button
          variant="contained"
          startIcon={<Payment />}
          onClick={() => navigate('/agente/pagar')}
          sx={{
            bgcolor: 'white',
            color: 'primary.main',
            fontWeight: 700,
            borderRadius: 2,
            px: 2.5,
            '&:hover': { bgcolor: 'action.hover' },
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          Nova Transacção
        </Button>
      </Box>

      {/* Wallet balance card */}
      {wallet && (
        <Card
          variant="outlined"
          sx={{
            mb: 3,
            borderRadius: 3,
            borderColor: isLowBalance ? 'error.main' : 'primary.200',
            bgcolor: isLowBalance ? 'error.50' : 'background.paper',
            boxShadow: isLowBalance
              ? '0 0 0 2px rgba(211,47,47,0.15)'
              : '0 2px 8px rgba(0,0,0,0.06)',
          }}
        >
          <CardContent sx={{ p: '20px !important' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: isLowBalance ? 'error.main' : 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {isLowBalance
                  ? <Warning sx={{ color: 'white', fontSize: 32 }} />
                  : <AccountBalanceWallet sx={{ color: 'white', fontSize: 32 }} />
                }
              </Box>
              <Box flex={1} minWidth={0}>
                <Typography variant="body2" color="text.secondary" fontWeight={500} mb={0.25}>
                  Saldo do Float (Wallet)
                </Typography>
                <Typography
                  variant="h4"
                  fontWeight={800}
                  color={isLowBalance ? 'error.main' : 'primary.main'}
                  sx={{ lineHeight: 1.1, mb: 0.5 }}
                >
                  {fmt(wallet.saldo)}
                </Typography>
                {wallet.saldoMinimo > 0 && (
                  <>
                    <LinearProgress
                      variant="determinate"
                      value={walletPercent}
                      color={isLowBalance ? 'error' : 'primary'}
                      sx={{ height: 6, borderRadius: 4, mb: 0.5, maxWidth: 300 }}
                    />
                    <Typography variant="caption" color={isLowBalance ? 'error.main' : 'text.secondary'}>
                      {isLowBalance
                        ? `Saldo abaixo do mínimo (${fmt(wallet.saldoMinimo)}) — Recarregue o float`
                        : `Mínimo requerido: ${fmt(wallet.saldoMinimo)}`}
                    </Typography>
                  </>
                )}
              </Box>
              <Chip
                label={wallet.estado}
                color={wallet.estado === 'ATIVO' ? 'success' : 'error'}
                sx={{ fontWeight: 600, flexShrink: 0 }}
              />
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Stats cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {statCards.map(s => (
          <Grid size={{ xs: 12, sm: 6, lg: 3 }} key={s.label}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: 2.5,
                height: '100%',
                transition: 'box-shadow 0.2s',
                '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.1)' },
              }}
            >
              <CardContent sx={{ p: '16px !important' }}>
                <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1.5,
                      bgcolor: s.iconBg,
                      display: 'flex',
                      color: `${s.color}.main`,
                      flexShrink: 0,
                    }}
                  >
                    {s.icon}
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={500} display="block">
                      {s.label}
                    </Typography>
                    <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                      {s.value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {s.sub}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Recent transactions */}
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Typography variant="h6" fontWeight={700}>
            Últimas Transacções
          </Typography>
          <Button
            size="small"
            endIcon={<ArrowForward fontSize="small" />}
            onClick={() => navigate('/agente/transacoes')}
          >
            Ver todas
          </Button>
        </Stack>

        <Card variant="outlined" sx={{ borderRadius: 2.5 }}>
          {transacoes.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <TrendingUp sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body1" color="text.secondary" fontWeight={500}>
                Sem transacções recentes
              </Typography>
              <Typography variant="body2" color="text.disabled" mb={2}>
                As suas transacções aparecerão aqui após processar o primeiro pagamento.
              </Typography>
              <Button variant="outlined" startIcon={<Payment />} onClick={() => navigate('/agente/pagar')}>
                Processar Pagamento
              </Button>
            </Box>
          ) : (
            transacoes.map((t, idx) => (
              <React.Fragment key={t.ID}>
                {idx > 0 && <Divider />}
                <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                  {/* Entity avatar */}
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: 'primary.100',
                      color: 'primary.main',
                      fontSize: 14,
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    {t.ENTIDADE_CODIGO.substring(0, 2)}
                  </Avatar>

                  {/* Middle: entity + reference */}
                  <Box flex={1} minWidth={0}>
                    <Typography variant="body2" fontWeight={700} noWrap sx={{ fontFamily: 'monospace' }}>
                      {t.ENTIDADE_CODIGO}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap display="block">
                      Ref: {t.REFERENCIA}
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      {fmtDate(t.DT_TRANSACAO)}
                    </Typography>
                  </Box>

                  {/* Right: amount + commission + status */}
                  <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                    <Typography variant="body2" fontWeight={700}>
                      {fmt(t.MONTANTE)}
                    </Typography>
                    <Chip
                      label={`+${fmt(t.COMISSAO_VALOR)}`}
                      size="small"
                      sx={{
                        bgcolor: 'success.50',
                        color: 'success.main',
                        fontWeight: 600,
                        fontSize: 10,
                        height: 18,
                        my: 0.25,
                      }}
                    />
                    <Box>
                      <Chip
                        label={ESTADO_LABEL[t.ESTADO] || t.ESTADO}
                        size="small"
                        color={ESTADO_COLOR[t.ESTADO] || 'default'}
                        sx={{ fontSize: 10, height: 18 }}
                      />
                    </Box>
                  </Box>
                </Box>
              </React.Fragment>
            ))
          )}
        </Card>
      </Box>
    </Box>
  );
}
